import { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { formatAddress } from "@medoma/models/addresses";
import { dateInputSchema } from "@medoma/models/date-and-time";
import { primaryPatientIdentifierType } from "@medoma/models/organizations";
import {
  patientNameType,
  personalIdentityNumberTypeSchema,
} from "@medoma/models/patients";
import type { IPatientType } from "@medoma/models/patients";
import { standardizePhoneNumber } from "@medoma/models/phoneNumbers";
import { conventionalPostalCodeSchema } from "@medoma/models/postalCode";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import Form from "@/components/Form/Form";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  informationFieldConfigValueSchema,
  normalizePatientIdentifier,
  registerNewProspectCareEpisode,
  sparPersonQueryOptions,
  useEconomicUnits,
  usePatientInformationFields,
  usePatientTypes,
  useTeams,
} from "@/api/Patients";
import type {
  IInformationFieldsConfig,
  INewProspectCareEpisode,
  ISparPerson,
} from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import { generateRandomUUID } from "@/Utils/UniqueId";
import AddressInput, { type AddressInputFields } from "./AddressInput";
import AreaSelect, { type AreaSelectFields } from "./AreaSelect";
import CareSummaryInput, {
  type CareSummaryInputFields,
} from "./CareSummaryInput";
import EconomicUnitSelect, {
  type EconomicUnitSelectFields,
} from "./EconomicUnitSelect";
import ExternalCareEpisodeId, {
  type ExternalCareEpisodeIdInputFields,
} from "./ExternalCareEpisodeIdInput";
import FlagsInput, { type FlagsInputFields } from "./FlagsInput";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "./PatientIdentifierInput";
import PatientNameInput, {
  type PatientNameInputFields,
} from "./PatientNameInput";
import PatientTypeInput, {
  type PatientTypeInputFields,
} from "./PatientTypeInput";
import PhoneNumberInput, {
  type PhoneNumberInputFields,
} from "./PhoneNumberInput";
import PlaceOfAssessmentInput, {
  type PlaceOfAssessmentInputFields,
} from "./PlaceOfAssessmentInput";
import PlannedAdmissionDateTimeInput, {
  type PlannedAdmissionDateTimeInputFields,
} from "./PlannedAdmissionDateTimeInput";
import ReasonForAdmissionInput, {
  type ReasonForAdmissionInputFields,
} from "./ReasonForAdmissionInput";
import ReferringUnitInput, {
  type ReferringUnitInputFields,
} from "./ReferringUnitInput";
import TeamSelect, { type TeamSelectFields } from "./TeamSelect";
import WardInput, { type WardInputFields } from "./WardInput";

type RegisterNewPatientFormFields = AddressInputFields &
  AreaSelectFields &
  CareSummaryInputFields &
  EconomicUnitSelectFields &
  ExternalCareEpisodeIdInputFields &
  FlagsInputFields &
  PatientIdentifierInputFields &
  PatientNameInputFields &
  PatientTypeInputFields &
  PhoneNumberInputFields &
  PlaceOfAssessmentInputFields &
  PlannedAdmissionDateTimeInputFields &
  ReasonForAdmissionInputFields &
  ReferringUnitInputFields &
  TeamSelectFields &
  WardInputFields;

// The identifier is carried in the query string by the identifier check that decided this
// person has no care episode yet. Which parameter it arrives under is what tells us the
// identifier's type.
const identifierFromSearchParams = (searchParams: URLSearchParams) => {
  const swedishIdentityNumber = searchParams.get("swedish-identity-number");
  const swissEMedicusNumber = searchParams.get("swiss-emedicus-number");
  const swissPatientNumber = searchParams.get("swiss-patient-number");
  const latvianPersonasKods = searchParams.get("latvian-personas-kods");
  const temporaryNumber = searchParams.get("temporary-number");

  // The identifier is required (and expected to be provided). If it is not provided, there
  // is no way forward, and we want to know about this to fix the issue. I.e. crash the app.
  const identifier = z
    .string()
    .parse(
      swedishIdentityNumber ??
        swissEMedicusNumber ??
        swissPatientNumber ??
        latvianPersonasKods ??
        temporaryNumber,
    );

  const personalIdentityNumberType = (() => {
    if (temporaryNumber !== null) {
      return personalIdentityNumberTypeSchema.enum
        .SwedishTemporaryIdentityNumber;
    }
    if (swedishIdentityNumber !== null) {
      return personalIdentityNumberTypeSchema.enum
        .SwedishPersonalIdentityNumber;
    }
    if (swissEMedicusNumber !== null) {
      return personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber;
    }
    if (swissPatientNumber !== null) {
      return personalIdentityNumberTypeSchema.enum.SwissPatientNumber;
    }
    if (latvianPersonasKods !== null) {
      return personalIdentityNumberTypeSchema.enum.LatvianPersonasKods;
    }
    throw new Error("No valid identifier type found");
  })();

  return { identifier, personalIdentityNumberType };
};

export const RegisterNewPatientFormContainer = () => {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const { identifier, personalIdentityNumberType } =
    identifierFromSearchParams(searchParams);

  // SPAR (Swedish population register) is the source of truth for name and address, and for
  // a patient we have never met it is the only source we have. It only holds Swedish
  // personal identity numbers, so skip it for other identifiers.
  const isSwedishPersonalIdentityNumber =
    personalIdentityNumberType ===
    personalIdentityNumberTypeSchema.enum.SwedishPersonalIdentityNumber;

  const { data: isSparEnabled, isPending: isPendingSparFlag } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.UseSparForPatientRegistration,
  );

  const { data: sparPerson, isLoading: isLoadingSparPerson } = useQuery({
    ...sparPersonQueryOptions({
      pin: normalizePatientIdentifier({
        identifier,
        identifierType: personalIdentityNumberType,
      }),
    }),
    enabled: isSwedishPersonalIdentityNumber && isSparEnabled === true,
    // SPAR is enrichment, not required: a failure must degrade to an empty form rather than
    // bubble to an error boundary and crash the page.
    throwOnError: false,
  });

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  // Both are read here rather than in the form so the defaults below can be derived instead
  // of written into the form after the fact.
  const {
    data: economicUnits,
    isPending: isPendingEconomicUnits,
    isError: isErrorEconomicUnits,
    error: errorEconomicUnits,
  } = useEconomicUnits();

  const {
    data: patientTypes,
    isPending: isPendingPatientTypes,
    isError: isErrorPatientTypes,
    error: errorPatientTypes,
  } = usePatientTypes();

  // Organization configuration, which the default values below are derived from. The SPAR
  // flag is waited for here rather than with the lookup itself: until the flag has answered,
  // the lookup is disabled and reports nothing to wait for.
  if (isPendingSparFlag || isPendingEconomicUnits || isPendingPatientTypes) {
    return <Loading message={t`Hämtar uppgifter om enheten`} />;
  }

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  // The SPAR person query gates on `isLoading` (not `isPending`) because when it is disabled
  // (non-Swedish identifier or flag off) it stays pending forever and `isPending` would
  // block the form.
  if (isLoadingSparPerson) {
    return <Loading message={t`Hämtar folkbokförda uppgifter`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  if (isErrorEconomicUnits) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta ekonomiska enheter.`} ${deducedError(errorEconomicUnits)}`}
      />
    );
  }

  if (isErrorPatientTypes) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienttyper.`} ${deducedError(errorPatientTypes)}`}
      />
    );
  }

  return (
    <RegisterNewPatientForm
      identifier={identifier}
      personalIdentityNumberType={personalIdentityNumberType}
      sparPerson={sparPerson}
      conditionalInformationFields={conditionalInformationFields}
      // A site with a single economic unit never asks which one; a site with several leaves
      // the choice to the user. Sites that do not use economic units are served a single
      // default one, so they take the first branch.
      defaultEconomicUnitId={
        economicUnits.length === 1 && economicUnits[0]
          ? economicUnits[0].id.toString()
          : ""
      }
      // Matches the register form this replaces, which arrives with the first available
      // patient type already selected.
      defaultPatientType={patientTypes[0]}
    />
  );
};

const RegisterNewPatientForm = ({
  identifier,
  personalIdentityNumberType,
  sparPerson,
  conditionalInformationFields,
  defaultEconomicUnitId,
  defaultPatientType,
}: {
  identifier: string;
  personalIdentityNumberType: z.infer<typeof personalIdentityNumberTypeSchema>;
  sparPerson: ISparPerson | null | undefined;
  conditionalInformationFields: IInformationFieldsConfig;
  defaultEconomicUnitId: string;
  defaultPatientType: IPatientType | undefined;
}) => {
  const { t } = useLingui();
  const { countryCode, organization } = useUserProfile();
  const navigate = useNavigate();

  const isTemporaryNumber =
    personalIdentityNumberType ===
    personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber;

  // The identifier arrives from the check that decided this person has no care episode yet,
  // but it is only a default: both it and the temporary-number choice stay editable, so the
  // type follows the checkbox rather than the parameter we arrived on. A number that is not
  // temporary is of the organization's own kind, which is what the input validates against.
  const submittedIdentifierType = (isTemporary: boolean) =>
    isTemporary
      ? personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber
      : primaryPatientIdentifierType(organization);

  const methods = useForm<RegisterNewPatientFormFields>({
    defaultValues: {
      identifier,
      isTemporaryNumber,
      givenName: sparPerson?.givenName ?? "",
      familyName: sparPerson?.familyName ?? "",
      // SPAR splits the street across Utdelningsadress1/2 (-> addressLine1/2). For short
      // addresses (the common case) the street sits in addressLine2 and addressLine1 is
      // empty, so prefer addressLine2 for our single field.
      addressLine1:
        sparPerson?.address?.addressLine2 ??
        sparPerson?.address?.addressLine1 ??
        "",
      postalCode: sparPerson?.address?.postalCode ?? "",
      city: sparPerson?.address?.city ?? "",
      // No coordinates are known yet, and the address input reads 0,0 as exactly that: it
      // keeps the coordinate fields disabled and the map centred on the care hub until
      // geocoding street and postal code has produced a position. The field is not optional,
      // so leaving it out would instead break the input on its first render.
      coordinates: { longitude: 0, latitude: 0 },
      economicUnitId: defaultEconomicUnitId,
      patientType: defaultPatientType,
      plannedAdmissionDateTime: "",
    },
  });

  const { setError, reset, getValues } = methods;

  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: wardEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CardiologyWorkflow,
  );

  const { data: teams } = useTeams();
  const { data: economicUnits } = useEconomicUnits();

  const { mutate, isPending: isPendingRegisterNewProspect } = useMutation({
    mutationFn: (careEpisode: INewProspectCareEpisode) =>
      registerNewProspectCareEpisode(careEpisode),
    onSuccess: (_, careEpisode) => {
      void navigate(`/commandcenter/patients/${careEpisode.careEpisodeId}`, {
        replace: true,
      });
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
  });

  // Fields the organization can switch on. Registration collects the ones that describe how
  // the patient got here; the rest are asked for at admission.
  const otherInformationFields = [
    {
      condition:
        conditionalInformationFields.ReferringUnit !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReferringUnitInput
          referringUnitFieldConfig={conditionalInformationFields.ReferringUnit}
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PlaceOfAssessment !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PlaceOfAssessmentInput
          placeOfAssessmentFieldConfig={
            conditionalInformationFields.PlaceOfAssessment
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.PlannedAdmissionDateTime !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <PlannedAdmissionDateTimeInput
          plannedAdmissionDateTimeFieldConfig={
            conditionalInformationFields.PlannedAdmissionDateTime
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.Flags !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <FlagsInput flagsFieldConfig={conditionalInformationFields.Flags} />
      ),
    },
    {
      condition:
        conditionalInformationFields.ReasonForAdmission !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <ReasonForAdmissionInput
          reasonForAdmissionFieldConfig={
            conditionalInformationFields.ReasonForAdmission
          }
        />
      ),
    },
    {
      condition:
        conditionalInformationFields.CareSummary !==
        informationFieldConfigValueSchema.enum.hidden,
      component: (
        <CareSummaryInput
          careSummaryFieldConfig={conditionalInformationFields.CareSummary}
        />
      ),
    },
  ];

  const hasAnyOtherInformation = otherInformationFields.some(
    (field) => field.condition,
  );

  const validateAndSubmit = methods.handleSubmit(async (data) => {
    const careEpisode: INewProspectCareEpisode = {
      careEpisodeId: generateRandomUUID(),
      address: formatAddress({
        addressLine1: data.addressLine1,
        postalCode: conventionalPostalCodeSchema.shape[countryCode].parse(
          data.postalCode,
        ),
        city: data.city,
        coordinates: data.coordinates,
        // The address input calls the field `additionalAddressInformation`; the address
        // itself calls it `additionalInformation`.
        additionalInformation: data.additionalAddressInformation,
        keyToPatient: data.keyToPatient,
        countryCode,
      }),
      area: data.areaId ? Number(data.areaId) : null,
      team: data.teamId ? Number(data.teamId) : null,
      economicUnit: Number(data.economicUnitId),
      externalCareEpisodeId: data.externalCareEpisodeId,
      name: {
        type: patientNameType.enum.GivenAndFamily,
        givenName: data.givenName,
        familyName: data.familyName,
      },
      patientType: data.patientType,
      personalIdentityNumber: normalizePatientIdentifier({
        identifier: data.identifier,
        identifierType: submittedIdentifierType(data.isTemporaryNumber),
      }),
      personalIdentityNumberType: submittedIdentifierType(
        data.isTemporaryNumber,
      ),
      // Optional at registration, so an untouched field must travel as "not given" rather
      // than as an empty phone number.
      phoneNumber: data.phoneNumber
        ? standardizePhoneNumber({ countryCode, phoneNumber: data.phoneNumber })
        : null,
      ward: data.wardId ? Number(data.wardId) : null,
      careSummary: data.careSummary,
      flags: data.flags,
      placeOfAssessment: data.placeOfAssessment,
      plannedAdmissionDateTime: dateInputSchema.decode(
        data.plannedAdmissionDateTime,
      ),
      reasonForAdmission: data.reasonForAdmission,
      referringUnit: data.referringUnit,
    };

    mutate(careEpisode);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <PatientIdentifierInput />
        {/* ExternalCareEpisodeId is optional, but should be placed after the identifier input */}
        {conditionalInformationFields.ExternalCareEpisodeId !==
        informationFieldConfigValueSchema.enum.hidden ? (
          <ExternalCareEpisodeId
            externalCareEpisodeIdFieldConfig={
              conditionalInformationFields.ExternalCareEpisodeId
            }
          />
        ) : null}
        <PatientNameInput />
        {wardEnabled ? <WardInput /> : null}
        <PatientTypeInput />
        <PhoneNumberInput isRequired={false} />
        <AddressInput
          additionalAddressInformationFieldConfig={
            conditionalInformationFields.AdditionalAdressInformation
          }
          keyToPatientFieldConfig={conditionalInformationFields.KeyToPatient}
        />
        {areaEnabled ? <AreaSelect /> : null}
        {teams && teams.length > 0 ? <TeamSelect /> : null}
        {/* A site with exactly one economic unit has it filled in already. */}
        {economicUnits && economicUnits.length > 1 ? (
          <EconomicUnitSelect />
        ) : null}
        {hasAnyOtherInformation ? (
          <Fieldset legend={t`Övrig information`} visualStyle="framed">
            <Form.Section>
              {otherInformationFields
                .filter((field) => field.condition)
                .map((field, index) => (
                  <Fragment key={index}>{field.component}</Fragment>
                ))}
            </Form.Section>
          </Fieldset>
        ) : null}
        {methods.formState.errors.root?.server?.message ? (
          <ErrorMessage
            message={methods.formState.errors.root.server.message}
          />
        ) : null}

        <FilledButton type="submit" disabled={isPendingRegisterNewProspect}>
          {isPendingRegisterNewProspect ? <Spinner /> : null}
          <Trans>Registrera patient</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};
